/*
 * In-app navigation guards. Unlike onBeforeUnload, which can only ask for the
 * browser's generic "leave site?" box (and forces a full page load to get
 * it), a guard stops the router's navigation outright and takes over: it
 * shows its own UI and later resumes with `navigateUnguarded`. Covers every
 * router.goto, including the router's global link-click handler. Closing the
 * tab, typing a URL and the back button are not covered.
 *
 * A guard returns true when it has taken the navigation.
 */
type NavigateGuard = (url: string) => boolean;
const navigateGuards = new Set<NavigateGuard>();
let isUnguarded = false;

export function onBeforeNavigate(guard: NavigateGuard) {
  navigateGuards.add(guard);
  return () => {
    navigateGuards.delete(guard);
  };
}

export function runNavigateGuards(url: string) {
  if (isUnguarded) {
    return false;
  }
  for (const guard of navigateGuards) {
    if (guard(url)) {
      return true;
    }
  }
  return false;
}

/** Navigate past every guard, e.g. once a guard's own dialog is answered. */
export function navigateUnguarded(go: () => void) {
  isUnguarded = true;
  try {
    go();
  } finally {
    isUnguarded = false;
  }
}
